import {
  ImapEncryptionEnum,
  type InputUpdateWorkspaceAutoExpenseEmail,
  type WorkspaceAutoExpenseEmailSettings,
} from "@src/__generated__/urql-graphql";
import { required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { action, makeObservable, observable } from "mobx";

export class ExpenseInboxIntegrationState {
  @observable.ref testResult: string | null = null;
  /**
   * This is used to determine if the password field is required.
   * It is set to true when the user is setting up the IMAP integration.
   * It is set to false when the user is editing the IMAP integration.
   */
  @observable.ref isPasswordRequired = true;

  private readonly requiredIfActive = (value: string | ImapEncryptionEnum) => {
    if (this.form.$.is_active.value) return required(value);
    return null;
  };

  private readonly passwordRequiredForImapSetup = (value: string) => {
    if (this.isPasswordRequired) return required(value);
    return null;
  };

  readonly form = new FormState({
    imap_host: new FieldState("").validators(this.requiredIfActive),
    imap_port: new FieldState("").validators(this.requiredIfActive),
    imap_username: new FieldState("").validators(this.requiredIfActive),
    imap_password: new FieldState("").validators(
      this.passwordRequiredForImapSetup,
    ),
    imap_encryption: new FieldState<ImapEncryptionEnum>(
      ImapEncryptionEnum.Ssl,
    ).validators(this.requiredIfActive),
    is_active: new FieldState(false),
  });

  constructor() {
    makeObservable(this);
  }

  @action.bound init(
    settings: WorkspaceAutoExpenseEmailSettings | null | undefined,
  ) {
    if (!settings) {
      this.isPasswordRequired = true;
      this.form.reset();
      return;
    }

    this.isPasswordRequired = false;
    this.form.$.imap_host.onChange(settings.imap_host);
    this.form.$.imap_port.onChange(settings.imap_port.toString());
    this.form.$.imap_username.onChange(settings.imap_username);
    this.form.$.imap_encryption.onChange(settings.imap_encryption);
    this.form.$.is_active.onChange(settings.is_active);
  }

  @action.bound serialize(): InputUpdateWorkspaceAutoExpenseEmail | null {
    if (!this.form.$.is_active.$) return null;
    return {
      imap_host: this.form.$.imap_host.$,
      imap_port: Number(this.form.$.imap_port.$),
      imap_username: this.form.$.imap_username.$,
      imap_password: this.form.$.imap_password.$,
      imap_encryption: this.form.$.imap_encryption.$,
      is_active: this.form.$.is_active.$,
    };
  }

  @action.bound setTestResult(result: string | null) {
    this.testResult = result;
  }
}
